import React from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, IconButton, Stack, Typography } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import OwnerMonthGrid from "./calendar/OwnerMonthGrid";
import OwnerDaySheet from "./calendar/OwnerDaySheet";
import OwnerTaskDialog from "./calendar/OwnerTaskDialog";
import { OwnerGoogleCalendarDialog, OwnerGoogleEventDialog } from "./calendar/OwnerGoogleCalendarDialogs";
import OwnerRoutineDialog from "./calendar/OwnerRoutineDialog";

function localYmd(value = new Date()) { const date = new Date(value.getTime() - value.getTimezoneOffset() * 60000); return date.toISOString().slice(0, 10); }
function shiftDate(dateKey, delta) { const date = new Date(`${dateKey}T12:00:00`); date.setDate(date.getDate() + delta); return localYmd(date); }
function shiftMonth(month, delta) { const [year, monthNumber] = String(month).split("-").map(Number); const date = new Date(year, monthNumber - 1 + delta, 1, 12, 0, 0, 0); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`; }
function shortDateLabel(dateKey) { const date = new Date(`${dateKey}T12:00:00`); return new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "numeric", month: "short" }).format(date); }

export default function OwnerCalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = React.useMemo(() => localYmd(), []);
  const requestedDate = /^\d{4}-\d{2}-\d{2}$/.test(String(searchParams.get("fecha") || "")) ? String(searchParams.get("fecha")) : today;
  const requestedView = ["mes","semana"].includes(searchParams.get("vista")) ? searchParams.get("vista") : "dia";
  const [selectedDate, setSelectedDate] = React.useState(requestedDate);
  const [month, setMonth] = React.useState(requestedDate.slice(0, 7));
  const [view, setView] = React.useState(requestedView);
  const [calendar, setCalendar] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dialog, setDialog] = React.useState("");
  const [exportingAgenda, setExportingAgenda] = React.useState(false);
  const [exportMessage, setExportMessage] = React.useState("");

  const syncUrl = React.useCallback((date, nextView = view) => {
    const next = new URLSearchParams(); next.set("fecha", date); if (nextView !== "dia") next.set("vista", nextView); setSearchParams(next, { replace: true });
  }, [setSearchParams, view]);

  const request=React.useRef({sequence:0,controller:null,busy:false});
  const cache=React.useRef(new Map());
  const load=React.useCallback(async({quiet=false,synchronize=false,force=false}={})=>{
    if(quiet&&!force&&request.current.busy)return;
    request.current.controller?.abort();
    const controller=new AbortController(),sequence=++request.current.sequence;
    request.current.controller=controller;request.current.busy=true;
    const apiView=({dia:'day',semana:'week',mes:'month'})[view];
    const key=`${view}:${month}:${selectedDate}`;
    if(!quiet){setLoading(true);setError('');if(cache.current.has(key))setCalendar(cache.current.get(key));}
    try{
      const response=synchronize
        ?await ownerApi.reconcileCalendar({collectionHorizonDays:7,date:selectedDate,view:apiView,tzOffsetMinutes:new Date().getTimezoneOffset()})
        :await ownerApi.calendar({month,date:selectedDate,view:apiView},{signal:controller.signal});
      if(controller.signal.aborted||sequence!==request.current.sequence)return;
      cache.current.set(key,response.calendar);if(cache.current.size>10)cache.current.delete(cache.current.keys().next().value);
      setCalendar(response.calendar);
    }catch(err){if(!controller.signal.aborted&&sequence===request.current.sequence&&!quiet)setError(formatApiError(err,'No pude cargar la agenda.'));}
    finally{if(sequence===request.current.sequence){request.current.busy=false;setLoading(false);}}
  },[month,selectedDate,view]);
  React.useEffect(()=>{load();return()=>request.current.controller?.abort();},[load]);
  React.useEffect(()=>{
    if(view!=='dia'||selectedDate!==today)return undefined;
    const timer=window.setInterval(()=>{if(document.visibilityState==='visible')load({quiet:true});},45000);
    return()=>window.clearInterval(timer);
  },[load,view,selectedDate,today]);
  const refresh=()=>{cache.current.clear();return load({quiet:true,force:true});};

  const selectDate = (date, forceDay = false) => {
    setSelectedDate(date); if (date.slice(0, 7) !== month) setMonth(date.slice(0, 7)); const nextView = forceDay ? "dia" : view; if (forceDay) setView("dia"); syncUrl(date, nextView);
  };
  const moveMonth = (delta) => { const nextMonth = shiftMonth(month, delta); const nextDate = `${nextMonth}-01`; setMonth(nextMonth); setSelectedDate(nextDate); syncUrl(nextDate, "mes"); };
  const changeView = (nextView) => { setView(nextView); syncUrl(selectedDate, nextView); };

  // WP-STANDARD | AGENDA DIAGNOSTIC EXPORT
  // Status: UI-WIRED · READ-ONLY · SECRET-SAFE-BACKEND · TESTED-SYNTAX · v1
  // This intentionally exports only the Calendar/Inbox pipeline. The backend
  // redacts credentials first; the browser then removes unrelated Owner domains
  // so this file can be shared to debug cleanup/unification without exposing money.
  const exportAgendaDiagnostic = React.useCallback(async () => {
    setExportingAgenda(true);
    setExportMessage("");
    setError("");
    try {
      const response = await ownerApi.diagnosticExport({ daysBack:365, daysForward:365, limit:1000 });
      const diagnostic = response?.diagnostic || response || {};
      const agendaDiagnostic = {
        schema: "webprofit.owner.agenda-diagnostic",
        schemaVersion: 1,
        generatedAt: diagnostic.generatedAt || new Date().toISOString(),
        purpose: "Diagnóstico compartible del flujo Google Calendar -> Inbox/Commitments -> limpieza/materialización.",
        safety: diagnostic.safety || { secretsIncluded:false },
        exportWindow: diagnostic.exportWindow || null,
        runtimeContext: diagnostic.runtimeContext || null,
        authorityMap: diagnostic.authorityMap || null,
        origins: diagnostic.origins || null,
        calendarAndPlanning: diagnostic.calendarAndPlanning || null,
        capturesAndPendingHistory: diagnostic.capturesAndPendingHistory || null,
        learnedAndAudited: {
          decisionTraces: diagnostic.learnedAndAudited?.decisionTraces || null,
          actionAudits: diagnostic.learnedAndAudited?.actionAudits || null,
          technicalIncidents: diagnostic.learnedAndAudited?.technicalIncidents || null,
        },
        uiSnapshot: {
          selectedDate,
          month,
          view,
          currentAgenda: calendar,
          href: window.location.href,
        },
      };
      const blob = new Blob([JSON.stringify(agendaDiagnostic, null, 2)], { type:"application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      anchor.href = url;
      anchor.download = `webprofit-agenda-diagnostico-${stamp}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setExportMessage("Agenda exportada. Subime ese JSON y puedo revisar exactamente qué evento entra, qué se materializa y qué no se está limpiando.");
    } catch (err) {
      setError(formatApiError(err, "No pude exportar el diagnóstico de Agenda."));
    } finally {
      setExportingAgenda(false);
    }
  }, [calendar, month, selectedDate, view]);

  const moveVisible=delta=>{
    if(view==='mes'){moveMonth(delta);return;}
    selectDate(shiftDate(selectedDate,delta*(view==='semana'?7:1)));
  };
  const matching=calendar?.selectedDate===selectedDate&&(!calendar.view||calendar.view===({dia:'day',semana:'week',mes:'month'})[view]);
  return <Box sx={{width:'100%',minWidth:0,height:'100%',minHeight:0,overflowY:'auto',overflowX:'hidden',pb:2}}>
    <Stack spacing={1} sx={{position:'sticky',top:0,zIndex:5,bgcolor:'background.default',py:1}}>
      <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{fontWeight:850,fontSize:'1.15rem',mr:'auto'}}>Mi día</Typography>
        {['dia','semana','mes'].map(key=><Button key={key} variant={view===key?'contained':'outlined'} onClick={()=>changeView(key)} size="small" sx={{minHeight:38,textTransform:'none'}}>{({dia:'Día',semana:'Semana',mes:'Mes'})[key]}</Button>)}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={.5} useFlexGap flexWrap="wrap" sx={{minWidth:0}}>
        <IconButton aria-label="Fecha anterior" onClick={()=>moveVisible(-1)}><ChevronLeftRoundedIcon/></IconButton>
        <input aria-label="Fecha del calendario" type="date" value={selectedDate} onChange={e=>{if(e.target.value)selectDate(e.target.value);}} style={{font:'inherit',color:'inherit',background:'transparent',border:'1px solid #9996',borderRadius:7,padding:'8px',minWidth:155,maxWidth:180,flex:'1 0 155px'}}/>
        <IconButton aria-label="Fecha siguiente" onClick={()=>moveVisible(1)}><ChevronRightRoundedIcon/></IconButton>
        <Button onClick={()=>selectDate(today,true)} sx={{minWidth:44}}>Hoy</Button>
        <Button disabled={loading} onClick={()=>load()} sx={{minWidth:0,ml:'auto'}}>Actualizar</Button>
      </Stack>
    </Stack>
    {error?<Alert severity="error" sx={{my:1}}>{error}</Alert>:null}
    {exportMessage?<Alert severity="success" onClose={()=>setExportMessage('')} sx={{my:1}}>{exportMessage}</Alert>:null}
    {!matching?<DashboardLoadingState label="Cargando fecha…" minHeight={300}/>:view==='mes'?
      <Box sx={{height:{md:620},minWidth:0}}><OwnerMonthGrid month={calendar.month} days={calendar.days||[]} selectedDate={selectedDate} onSelectDate={date=>selectDate(date,true)}/></Box>
    :view==='semana'?<Stack spacing={1} sx={{mt:1}}>{(calendar.days||[]).map(day=><Box key={day.date} sx={{p:1.5,border:'1px solid',borderColor:'divider',borderRadius:2,minWidth:0,display:'flex',alignItems:'center',gap:1,flexWrap:'wrap'}}>
      <Box sx={{flex:'1 1 180px'}}><Typography sx={{fontWeight:800,textTransform:'capitalize'}}>{shortDateLabel(day.date)}</Typography><Typography variant="body2" color="text.secondary">{day.openCommitmentCount||0} pendientes · {day.googleEventCount||0} eventos · {day.routineCount||0} rutinas</Typography></Box>
      <Button onClick={()=>selectDate(day.date,true)}>Abrir día</Button>
    </Box>)}</Stack>:<OwnerDaySheet calendar={calendar} onNewTask={()=>setDialog('task')} onNewGoogleEvent={()=>setDialog('googleEvent')} onGoogleSettings={()=>setDialog('google')} onRoutine={()=>setDialog('routine')} onExportAgenda={exportAgendaDiagnostic} exportingAgenda={exportingAgenda} onChanged={refresh}/>}
    <Button size="small" onClick={()=>load({synchronize:true})} disabled={loading} sx={{mt:1,textTransform:'none'}}>Conciliar agenda y volver a cargar</Button>
    <OwnerTaskDialog open={dialog === "task"} onClose={()=>setDialog("")} onSaved={refresh} selectedDate={selectedDate}/>
    <OwnerGoogleCalendarDialog open={dialog === "google"} onClose={()=>setDialog("")} currentIntegration={calendar?.googleCalendar} onChanged={refresh}/>
    <OwnerGoogleEventDialog open={dialog === "googleEvent"} onClose={()=>setDialog("")} selectedDate={selectedDate} onSaved={refresh}/>
    <OwnerRoutineDialog open={dialog === "routine"} onClose={()=>setDialog("")} calendar={calendar} onSaved={refresh}/>
  </Box>;
}
